import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'test-core-resizable-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true
})
export class ResizableLayoutComponent implements OnInit, OnDestroy {
  @Input() minSize = 32;
  @Input() maxSize = 400;

  private readonly _destroyRef = inject(DestroyRef);
  private currentResizeType: 'top' | 'bottom' | 'left' | 'right' | null = null;
  private startPos = { x: 0, y: 0 };
  private startSize = 0;
  private mouseMove$: Subscription | null = null;
  private mouseUp$: Subscription | null = null;
  private resizingElement: HTMLElement | null = null;

  constructor(private readonly renderer: Renderer2) {}

  ngOnInit() {
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        // ...existing logic...
      });
  }

  ngOnDestroy(): void {
    this.stopResize();
  }

  startResize(
    event: MouseEvent,
    type: 'top' | 'bottom' | 'left' | 'right'
  ): void {
    event.preventDefault();
    this.currentResizeType = type;

    this.startPos = {
      x: event.clientX,
      y: event.clientY
    };

    const element = (event.target as HTMLElement).closest(
      `.${type}-bar`
    ) as HTMLElement;
    if (!element) return;

    this.resizingElement = element;
    this.startSize =
      type === 'left' || type === 'right'
        ? element.offsetWidth
        : element.offsetHeight;

    this.mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((e) => this.onResize(e));

    this.mouseUp$ = fromEvent(document, 'mouseup').subscribe(() =>
      this.stopResize()
    );

    this.renderer.addClass(document.body, 'resizing');
  }

  private onResize(event: MouseEvent): void {
    if (!this.currentResizeType || !this.resizingElement) return;

    let newSize: number;

    switch (this.currentResizeType) {
      case 'left':
        newSize = this.startSize + (event.clientX - this.startPos.x);
        break;
      case 'right':
        newSize = this.startSize - (event.clientX - this.startPos.x);
        break;
      case 'top':
        newSize = this.startSize + (event.clientY - this.startPos.y);
        break;
      case 'bottom':
        newSize = this.startSize - (event.clientY - this.startPos.y);
        break;
      default:
        return;
    }

    newSize = Math.max(this.minSize, Math.min(this.maxSize, newSize));

    const sizeProp = this.isHorizontalResize() ? 'width' : 'height';
    this.renderer.setStyle(this.resizingElement, sizeProp, `${newSize}px`);
  }

  private stopResize(): void {
    this.currentResizeType = null;
    this.resizingElement = null;

    if (this.mouseMove$) {
      this.mouseMove$.unsubscribe();
      this.mouseMove$ = null;
    }
    if (this.mouseUp$) {
      this.mouseUp$.unsubscribe();
      this.mouseUp$ = null;
    }

    this.renderer.removeClass(document.body, 'resizing');
  }

  private isHorizontalResize(): boolean {
    return (
      this.currentResizeType === 'left' || this.currentResizeType === 'right'
    );
  }
}
